// @ts-nocheck
import Vue from 'vue';
import { ProjectGemsService, ProjectGemsServiceError } from '@/services/projectGemsService';
import { GemsService } from '@/services/gemsService';
import { gemTypeRegistry } from '@/services/GemTypeRegistry';
import { GemNavigationService } from '@/services/gemNavigationService';
import DynamicGemTypeCard from './DynamicGemTypeCard.vue';
import GemTooltip from './GemTooltip.vue';
import { store } from '@/stores';
export default Vue.extend({
    name: 'UniversalGemsDisplay',
    components: {
        DynamicGemTypeCard,
        GemTooltip
    },
    props: {
        // Operating mode
        mode: {
            type: String,
            required: true,
            validator: (value) => ['project', 'session'].includes(value)
        },
        // Project mode props
        projectId: {
            type: String,
            default: null
        },
        projectName: {
            type: String,
            default: null
        },
        visible: {
            type: Boolean,
            default: true
        },
        // Session mode props
        gems: {
            type: Object,
            default: null
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: null
        },
        // Common props
        title: {
            type: String,
            default: null
        },
        className: {
            type: String,
            default: ''
        },
        maxWidth: {
            type: Number,
            default: 320
        }
    },
    data() {
        return {
            // Internal state for project mode
            internalGems: null,
            isInternalLoading: false,
            internalError: null,
            isRetrying: false,
            // Common state
            collapsedStates: {},
            canScrollLeft: false,
            canScrollRight: false,
            renderKey: 0,
            // Tooltip state (project mode only)
            tooltipVisible: false,
            tooltipItem: null,
            tooltipGemType: null,
            tooltipTarget: null,
            // Store reference
            store
        };
    },
    computed: {
        // Unified data source
        effectiveGems() {
            return this.mode === 'project' ? this.internalGems : this.gems;
        },
        // Unified loading state
        effectiveLoading() {
            return this.mode === 'project' ? this.isInternalLoading : this.isLoading;
        },
        // Unified error state
        effectiveError() {
            return this.mode === 'project' ? this.internalError : this.error;
        },
        // Dynamic CSS classes
        containerClasses() {
            const baseClass = this.mode === 'project' ? 'project-gems-widget' : 'gems-container';
            return [
                baseClass,
                this.className,
                {
                    [`${baseClass}--loading`]: this.effectiveLoading,
                    [`${baseClass}--error`]: !!this.effectiveError,
                    [`${baseClass}--empty`]: !this.hasGems,
                    [`${baseClass}--has-content`]: this.hasGems,
                    [`${baseClass}--hidden`]: this.mode === 'project' && !this.visible
                }
            ];
        },
        // CSS class prefix for child elements
        classPrefix() {
            return this.mode === 'project' ? 'project-gems' : 'gems';
        },
        // Dynamic title
        displayTitle() {
            if (this.title)
                return this.title;
            return this.mode === 'project'
                ? `💎${this.projectName} Gems`
                : '💎Session Gems';
        },
        // Dynamic text content
        loadingText() {
            return this.mode === 'project' ? 'Loading project gems...' : 'Loading gems...';
        },
        errorTitle() {
            return this.mode === 'project' ? 'Failed to load project gems' : 'Failed to load gems';
        },
        emptyText() {
            return this.mode === 'project'
                ? 'No gems found in this project'
                : 'No gems available for this session';
        },
        scrollAriaLabel() {
            return this.mode === 'project' ? 'Project gems cards' : 'Gems cards';
        },
        // Gem data computed properties
        hasGems() {
            try {
                return !!(this.effectiveGems && Object.keys(this.effectiveGems).length > 0);
            }
            catch (error) {
                return false;
            }
        },
        totalGemsCount() {
            try {
                if (this.mode === 'project') {
                    return this.effectiveGems ? Object.values(this.effectiveGems).reduce((sum, items) => sum + items.length, 0) : 0;
                }
                else {
                    return GemsService.countTotalGems(this.effectiveGems);
                }
            }
            catch (error) {
                return 0;
            }
        },
        sortedGemTypes() {
            try {
                if (!this.effectiveGems)
                    return [];
                if (this.mode === 'project') {
                    return Object.keys(this.effectiveGems).sort();
                }
                else {
                    return GemsService.getSortedGemTypes(this.effectiveGems);
                }
            }
            catch (error) {
                return [];
            }
        },
        allCollapsed() {
            return this.sortedGemTypes.every(type => this.collapsedStates[type]);
        },
        allExpanded() {
            return this.sortedGemTypes.every(type => !this.collapsedStates[type]);
        },
        showScrollIndicators() {
            return this.canScrollLeft || this.canScrollRight;
        }
    },
    watch: {
        // Project mode watchers
        visible: {
            handler(newVisible) {
                if (this.mode === 'project' && newVisible && this.projectId) {
                    this.loadProjectGems();
                }
            },
            immediate: true
        },
        projectId: {
            handler(newProjectId) {
                if (this.mode === 'project' && newProjectId && this.visible) {
                    this.loadProjectGems();
                }
            }
        },
        // Session mode watchers
        gems: {
            handler(newGems) {
                if (this.mode === 'session' && newGems) {
                    this.initializeCollapsedStates();
                    this.$nextTick(() => {
                        this.updateScrollState();
                    });
                }
            },
            immediate: true,
            deep: true
        },
        // Common watchers
        effectiveGems: {
            handler(newGems) {
                if (newGems) {
                    this.initializeCollapsedStates();
                    this.$nextTick(() => {
                        this.updateScrollState();
                    });
                }
            }
        }
    },
    mounted() {
        try {
            // Initialize scroll detection
            this.$nextTick(() => {
                this.updateScrollState();
            });
            // Session mode specific setup
            if (this.mode === 'session') {
                // Listen for gems update events for accessibility
                this.handleGemsUpdateForAccessibility = this.handleGemsUpdateForAccessibility.bind(this);
                window.addEventListener('gems-updated', this.handleGemsUpdateForAccessibility);
                // Listen for artifact received events to force immediate updates
                this.handleArtifactReceived = this.handleArtifactReceived.bind(this);
                window.addEventListener('gems-artifact-received', this.handleArtifactReceived);
                if (this.effectiveGems) {
                    this.$emit('gems-loaded', this.effectiveGems);
                }
            }
            // Project mode specific setup
            if (this.mode === 'project' && this.visible && this.projectId) {
                this.loadProjectGems();
            }
        }
        catch (error) {
            this.handleError(error);
        }
    },
    beforeDestroy() {
        // Clean up event listeners for session mode
        if (this.mode === 'session') {
            window.removeEventListener('gems-updated', this.handleGemsUpdateForAccessibility);
            window.removeEventListener('gems-artifact-received', this.handleArtifactReceived);
        }
    },
    methods: {
        // Data loading (project mode)
        async loadProjectGems() {
            if (!this.projectId)
                return;
            try {
                this.isInternalLoading = true;
                this.internalError = null;
                const gems = await ProjectGemsService.getProjectGems(this.projectId);
                this.internalGems = gems;
                this.$emit('gems-loaded', gems);
            }
            catch (error) {
                if (error instanceof ProjectGemsServiceError) {
                    this.internalError = error.message;
                }
                else {
                    this.internalError = 'Failed to load project gems';
                }
                this.$emit('error', error);
            }
            finally {
                this.isInternalLoading = false;
            }
        },
        // Gem data methods
        getItemsForType(type) {
            try {
                if (this.mode === 'project') {
                    return this.effectiveGems?.[type] || [];
                }
                else {
                    return GemsService.getGemsByType(this.effectiveGems, type);
                }
            }
            catch (error) {
                return [];
            }
        },
        getTypeConfig(type) {
            try {
                return gemTypeRegistry.getTypeConfig(type);
            }
            catch (error) {
                return gemTypeRegistry.getTypeConfig('FALLBACK');
            }
        },
        getCardKey(type) {
            if (this.mode === 'project') {
                return `project-${this.projectId}-${type}`;
            }
            else {
                // Session mode reactive key
                const items = this.getItemsForType(type);
                const itemCount = items ? items.length : 0;
                const flashingCount = items ? items.filter(item => store.gems.isGemFlashing(type, item.text)).length : 0;
                const isTypeFlashing = store.gems.isGemTypeFlashing(type) ? 1 : 0;
                return `${type}-${itemCount}-${flashingCount}-${isTypeFlashing}-${this.renderKey}`;
            }
        },
        getCardClass(type) {
            return this.mode === 'project' ? `project-gem-card-${type}` : `gem-card-${type}`;
        },
        // State management
        initializeCollapsedStates() {
            try {
                const states = {};
                this.sortedGemTypes.forEach(type => {
                    // Preserve existing state if it exists, otherwise start collapsed
                    states[type] = this.collapsedStates[type] !== undefined ? this.collapsedStates[type] : true;
                });
                this.collapsedStates = states;
            }
            catch (error) {
                // Silently handle errors
            }
        },
        // Session mode specific methods
        handleArtifactReceived(event) {
            if (this.mode !== 'session')
                return;
            // Force immediate re-render by updating the render key
            this.renderKey++;
            this.$forceUpdate();
            // Also trigger the accessibility update
            this.handleGemsUpdateForAccessibility(event);
        },
        handleGemsUpdateForAccessibility(event) {
            if (this.mode !== 'session')
                return;
            try {
                const { count, newGems } = event.detail;
                // Update the aria-live region for screen readers
                const liveRegion = this.$refs.liveRegion;
                if (liveRegion) {
                    const gemTypesList = newGems.map((gem) => gem.type || 'Unknown').join(', ');
                    liveRegion.textContent = `${count} new gems added: ${gemTypesList}`;
                    // Clear the message after a short delay to prevent repetition
                    setTimeout(() => {
                        liveRegion.textContent = '';
                    }, 3000);
                }
                // Scroll the first new gem into view
                this.$nextTick(() => {
                    const scrollContainer = this.$refs.scrollContainer;
                    if (scrollContainer && newGems.length > 0) {
                        // Find the gem card for the first new gem
                        const firstNewGem = newGems[0];
                        const gemType = firstNewGem.type;
                        if (gemType) {
                            const gemCard = scrollContainer.querySelector(`.gem-card-${gemType}`);
                            if (gemCard) {
                                // If gems panel is collapsed, flash the entire container
                                const allCollapsed = this.sortedGemTypes.every(type => this.collapsedStates[type]);
                                if (allCollapsed) {
                                    // Flash the gems container
                                    this.$el.classList.add('gems-container-flash');
                                    setTimeout(() => {
                                        this.$el.classList.remove('gems-container-flash');
                                    }, 600);
                                }
                                else {
                                    // Scroll the gem card into view
                                    gemCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                            }
                        }
                    }
                });
            }
            catch (error) {
                // Silently handle errors
            }
        },
        // Event handlers
        handleToggleCollapse(type) {
            try {
                this.$set(this.collapsedStates, type, !this.collapsedStates[type]);
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            }
            catch (error) {
                this.handleError(error);
            }
        },
        handleExpandAll() {
            try {
                this.sortedGemTypes.forEach(type => {
                    this.$set(this.collapsedStates, type, false);
                });
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            }
            catch (error) {
                this.handleError(error);
            }
        },
        handleCollapseAll() {
            try {
                this.sortedGemTypes.forEach(type => {
                    this.$set(this.collapsedStates, type, true);
                });
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            }
            catch (error) {
                this.handleError(error);
            }
        },
        handleItemHover(item, type) {
            // Item hover handling is delegated to parent components
        },
        handleShowTooltip(item, element, type) {
            try {
                // UNIVERSAL: handle tooltip internally for BOTH modes
                this.tooltipItem = item;
                this.tooltipGemType = type || null;
                this.tooltipTarget = element;
                this.tooltipVisible = true;
            }
            catch (error) {
                // Silently handle errors
            }
        },
        handleHideTooltip() {
            try {
                // UNIVERSAL: handle tooltip internally for BOTH modes
                this.tooltipVisible = false;
                this.tooltipItem = null;
                this.tooltipGemType = null;
                this.tooltipTarget = null;
            }
            catch (error) {
                // Silently handle errors
            }
        },
        handleCardError(error) {
            this.handleError(error);
            if (this.mode === 'project') {
                this.$emit('card-error', error);
            }
        },
        async handleItemNavigate(item) {
            try {
                const result = await GemNavigationService.handleGemNavigation(item, {
                    isProjectLevel: this.mode === 'project',
                    currentSessionId: this.$route?.params?.sessionId
                });
                if (!result.success) {
                    this.$emit('show-toast', {
                        message: result.message,
                        type: 'warning'
                    });
                }
            }
            catch (error) {
                this.$emit('show-toast', {
                    message: 'Failed to navigate to item',
                    type: 'error'
                });
            }
        },
        handleError(error) {
            this.$emit('error', error);
        },
        async handleRetry() {
            try {
                this.isRetrying = true;
                if (this.mode === 'project') {
                    await this.loadProjectGems();
                }
                else {
                    this.$emit('retry');
                    // Simulate retry delay
                    await new Promise(resolve => setTimeout(resolve, 1000));
                }
            }
            catch (error) {
                this.handleError(error);
            }
            finally {
                this.isRetrying = false;
            }
        },
        // Scroll handling
        updateScrollState() {
            try {
                const container = this.$refs.scrollContainer;
                if (!container)
                    return;
                this.canScrollLeft = container.scrollLeft > 0;
                this.canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth;
            }
            catch (error) {
                // Silently handle errors
            }
        },
        handleScroll() {
            this.updateScrollState();
        },
        scrollLeft() {
            try {
                const container = this.$refs.scrollContainer;
                if (!container)
                    return;
                container.scrollBy({ left: -600, behavior: 'smooth' });
            }
            catch (error) {
                // Silently handle errors
            }
        },
        scrollRight() {
            try {
                const container = this.$refs.scrollContainer;
                if (!container)
                    return;
                container.scrollBy({ left: 600, behavior: 'smooth' });
            }
            catch (error) {
                // Silently handle errors
            }
        }
    }
});
